import { MarketAnalysis, CompetitorProfile, GeographicCluster, MarketChallenge } from 'api/interfaces/MarketAnalysis'
import { AIPlanMarketAnalysisSection, MarketAnalysisCustomSectionPosition } from 'api/interfaces/AIPlanMarketAnalysisSection'
import { AIPlanCustomSection } from 'api/interfaces/AIPlan'

const renderField = (label: string, value: string | undefined): string => {
  if (!value) return ''
  return `### ${label}\n\n${value}\n\n`
}

const renderCompetitorProfiles = (profiles: CompetitorProfile[]): string => {
  if (!profiles?.length) return ''
  return profiles.map(p => {
    const parts = [`**${p.name}**\n`]
    if (p.strategicThreatLevel) parts.push(`**Threat Level:** ${p.strategicThreatLevel}\n`)
    if (p.overview) parts.push(`${p.overview}\n`)
    if (p.strengths) parts.push(`**Strengths:** ${p.strengths}\n`)
    if (p.weaknesses) parts.push(`**Weaknesses:** ${p.weaknesses}\n`)
    if (p.strategicImplications) parts.push(`**Strategic Implications:** ${p.strategicImplications}\n`)
    return parts.join('\n')
  }).join('\n')
}

const renderGeographicClusters = (clusters: GeographicCluster[]): string => {
  if (!clusters?.length) return ''
  return clusters.map(c => {
    const parts = [`**${c.clusterName}**\n`]
    if (c.priorityLevel) parts.push(`**Priority:** ${c.priorityLevel}\n`)
    if (c.alignmentScore) parts.push(`**Alignment Score:** ${c.alignmentScore}\n`)
    if (c.keyAreas) parts.push(`**Key Areas:** ${c.keyAreas}\n`)
    if (c.strategicRationale) parts.push(`${c.strategicRationale}\n`)
    return parts.join('\n')
  }).join('\n')
}

const renderMarketChallenges = (challenges: MarketChallenge[]): string => {
  if (!challenges?.length) return ''
  return challenges.map(c => {
    const parts = [`**${c.title}**\n`]
    if (c.severity) parts.push(`**Severity:** ${c.severity}\n`)
    if (c.description) parts.push(`${c.description}\n`)
    if (c.operationalImplications) parts.push(`**Operational Implications:** ${c.operationalImplications}\n`)
    return parts.join('\n')
  }).join('\n')
}

const renderCustomSections = (sections: AIPlanCustomSection[] | undefined, startNumber: number): string => {
  if (!sections?.length) return ''
  let num = startNumber
  return sections
    .filter(s => !s.hideAll)
    .map(s => {
      const parts: string[] = []
      if (s.showTitle !== false && s.title) {
        parts.push(`## ${num}. ${s.title}\n\n`)
      }
      num++
      if (s.content) parts.push(`${s.content}\n\n`)
      return parts.join('')
    })
    .join('')
}

export const marketAnalysisToMarkdown = (
  marketAnalysis: MarketAnalysis | undefined,
  section: AIPlanMarketAnalysisSection | null | undefined,
  companyName: string,
  introductionTitle?: string
): string => {
  if (!marketAnalysis) return ''

  const parts: string[] = []

  const title = introductionTitle || section?.introduction?.title || 'Market Analysis & Strategy'
  const introContent = section?.introduction?.content || `## ${companyName}\n\n---`
  if (!section?.introduction?.hideAll) {
    parts.push(`# ${title}\n\n${introContent}\n\n`)
  }

  const positions: MarketAnalysisCustomSectionPosition[] = [
    'aboveStrategicOverview', 'aboveIndustryContext', 'aboveCompetitiveLandscape',
    'aboveGeographicOpportunities', 'aboveSeasonalPatterns', 'aboveChallenges',
    'aboveInsightsSummary', 'aboveBottom'
  ]
  const standardSectionKeys = [
    'strategicOverview', 'industryContextAnalysis', 'competitiveLandscape',
    'geographicOpportunities', 'seasonalAndCyclicalPatterns', 'coreChallengesInMarket',
    'insightsSummary'
  ]

  const sectionNumbers: Record<string, number> = {}
  const customSectionStartNumbers: Record<string, number> = {}
  let counter = 1
  for (let i = 0; i < positions.length; i++) {
    const customCount = (section?.[positions[i]] as AIPlanCustomSection[] ?? []).filter(s => !s.hideAll).length
    customSectionStartNumbers[positions[i]] = counter
    counter += customCount
    if (i < standardSectionKeys.length) {
      sectionNumbers[standardSectionKeys[i]] = counter
      counter += 1
    }
  }

  const sectionTitleText = (_sectionName: string, titleKey: string, defaultTitle: string): string => {
    return (section as any)?.[titleKey] ?? defaultTitle
  }

  parts.push(renderCustomSections(section?.aboveStrategicOverview, customSectionStartNumbers['aboveStrategicOverview']))
  parts.push(`## ${sectionNumbers['strategicOverview']}. ${sectionTitleText('strategicOverview', 'strategicOverviewTitle', 'Strategic Overview')}\n\n`)
  parts.push(renderField('Business Positioning', marketAnalysis.strategicOverview?.businessPositioning))
  parts.push(renderField('Core Market Challenges & Opportunities', marketAnalysis.strategicOverview?.coreMarketChallengesAndOpportunities))
  parts.push(renderField('Analytical Framework', marketAnalysis.strategicOverview?.analyticalFramework))

  parts.push(renderCustomSections(section?.aboveIndustryContext, customSectionStartNumbers['aboveIndustryContext']))
  parts.push(`## ${sectionNumbers['industryContextAnalysis']}. ${sectionTitleText('industryContextAnalysis', 'industryContextAnalysisTitle', 'Industry Context Analysis')}\n\n`)
  parts.push(renderField('Market Size & Growth', marketAnalysis.industryContextAnalysis?.marketSizeAndGrowth))
  parts.push(renderField('Transformative Trends', marketAnalysis.industryContextAnalysis?.transformativeTrends))
  parts.push(renderField('Regulatory Considerations', marketAnalysis.industryContextAnalysis?.regulatoryConsiderations))
  parts.push(renderField('Seasonal Impacts', marketAnalysis.industryContextAnalysis?.seasonalImpacts))
  parts.push(renderField('Related Industry Influences', marketAnalysis.industryContextAnalysis?.relatedIndustryInfluences))
  parts.push(renderField('Economic Indicators', marketAnalysis.industryContextAnalysis?.economicIndicators))

  parts.push(renderCustomSections(section?.aboveCompetitiveLandscape, customSectionStartNumbers['aboveCompetitiveLandscape']))
  parts.push(`## ${sectionNumbers['competitiveLandscape']}. ${sectionTitleText('competitiveLandscape', 'competitiveLandscapeTitle', 'Competitive Landscape')}\n\n`)
  parts.push(renderField('Competitive Assessment Methodology', marketAnalysis.competitiveLandscape?.competitiveAssessmentMethodology))
  if (marketAnalysis.competitiveLandscape?.keyCompetitorProfiles?.length) {
    parts.push(`### Key Competitor Profiles\n\n`)
    parts.push(renderCompetitorProfiles(marketAnalysis.competitiveLandscape.keyCompetitorProfiles))
    parts.push('\n')
  }
  parts.push(renderField('Market Positioning Analysis', marketAnalysis.competitiveLandscape?.marketPositioningAnalysis))
  parts.push(renderField('Share of Voice Analysis', marketAnalysis.competitiveLandscape?.shareOfVoiceAnalysis))
  parts.push(renderField('Gap Analysis', marketAnalysis.competitiveLandscape?.gapAnalysis))
  parts.push(renderField('Competitive Positioning Opportunities', marketAnalysis.competitiveLandscape?.competitivePositioningOpportunities))

  parts.push(renderCustomSections(section?.aboveGeographicOpportunities, customSectionStartNumbers['aboveGeographicOpportunities']))
  parts.push(`## ${sectionNumbers['geographicOpportunities']}. ${sectionTitleText('geographicOpportunities', 'geographicOpportunitiesTitle', 'Geographic Opportunities')}\n\n`)
  parts.push(renderField('Geographic Targeting Methodology', marketAnalysis.geographicOpportunities?.geographicTargetingMethodology))
  parts.push(renderField('Regional Demand Analysis', marketAnalysis.geographicOpportunities?.regionalDemandAnalysis))
  if (marketAnalysis.geographicOpportunities?.opportunityScoringSystem?.length) {
    parts.push(`### Opportunity Scoring System\n\n`)
    parts.push(renderGeographicClusters(marketAnalysis.geographicOpportunities.opportunityScoringSystem))
    parts.push('\n')
  }
  parts.push(renderField('Competitive Density Analysis', marketAnalysis.geographicOpportunities?.competitiveDensityAnalysis))
  parts.push(renderField('Demographic Analysis', marketAnalysis.geographicOpportunities?.demographicAnalysis))
  parts.push(renderField('Environmental Considerations', marketAnalysis.geographicOpportunities?.environmentalConsiderations))

  parts.push(renderCustomSections(section?.aboveSeasonalPatterns, customSectionStartNumbers['aboveSeasonalPatterns']))
  parts.push(`## ${sectionNumbers['seasonalAndCyclicalPatterns']}. ${sectionTitleText('seasonalAndCyclicalPatterns', 'seasonalPatternsTitle', 'Seasonal & Cyclical Patterns')}\n\n`)
  parts.push(renderField('Key Seasonal Cycles', marketAnalysis.seasonalAndCyclicalPatterns?.keySeasonalCycles))
  parts.push(renderField('External Factor Analysis', marketAnalysis.seasonalAndCyclicalPatterns?.externalFactorAnalysis))
  parts.push(renderField('Demand Variation Quantification', marketAnalysis.seasonalAndCyclicalPatterns?.demandVariationQuantification))
  parts.push(renderField('Consumer Behavior Trends', marketAnalysis.seasonalAndCyclicalPatterns?.consumerBehaviorTrends))

  parts.push(renderCustomSections(section?.aboveChallenges, customSectionStartNumbers['aboveChallenges']))
  parts.push(`## ${sectionNumbers['coreChallengesInMarket']}. ${sectionTitleText('coreChallengesInMarket', 'coreChallengesTitle', 'Core Challenges in Market')}\n\n`)
  parts.push(renderField('Challenge Prioritization Analysis', marketAnalysis.coreChallengesInMarket?.challengePrioritizationAnalysis))
  parts.push(renderField('Market Constraint Analysis', marketAnalysis.coreChallengesInMarket?.marketConstraintAnalysis))
  if (marketAnalysis.coreChallengesInMarket?.coreChallengesInTheMarket?.length) {
    parts.push(`### Core Challenges\n\n`)
    parts.push(renderMarketChallenges(marketAnalysis.coreChallengesInMarket.coreChallengesInTheMarket))
    parts.push('\n')
  }

  parts.push(renderCustomSections(section?.aboveInsightsSummary, customSectionStartNumbers['aboveInsightsSummary']))
  parts.push(`## ${sectionNumbers['insightsSummary']}. ${sectionTitleText('insightsSummary', 'insightsSummaryTitle', 'Insights Summary')}\n\n`)
  parts.push(renderField('Key Findings Synthesis', marketAnalysis.insightsSummary?.keyFindingsSynthesis))
  parts.push(renderField('Strategic Direction', marketAnalysis.insightsSummary?.strategicDirection))
  parts.push(renderField('Business-Specific Strategic Insights', marketAnalysis.insightsSummary?.businessSpecificStrategicInsights))

  parts.push(renderCustomSections(section?.aboveBottom, customSectionStartNumbers['aboveBottom']))

  return parts.filter(Boolean).join('')
}
